import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

export interface ContractAmountColumn {
    key: string;
    title: string;
    dataIndex: string;
    sortable: boolean;
    filterable: boolean;
    width: number;
    align: 'left' | 'right';
    headerAlign: 'left' | 'right';
    render: (value: number | string) => string;
}

export interface ContractDateColumn {
    key: string;
    title: string;
    dataIndex: string;
    sortable: boolean;
    filterable: boolean;
    width: number;
    align: 'left' | 'right';
    headerAlign: 'left' | 'right';
    render: (date: string) => string;
}

/**
 * Shared amount columns for contract grids (SumainLei, SumainEuro).
 * Used in both Contracts.tsx and Payment.tsx.
 */
export const getContractAmountColumns = (
    t: TFunction,
    options?: { amountWidth?: number; amountAlign?: 'left' | 'right' },
): ContractAmountColumn[] => {
    const width = options?.amountWidth ?? 80;
    const align = options?.amountAlign ?? 'left';
    return [
        {
            key: 'SumainLei',
            title: t('contracts.amountLei'),
            dataIndex: 'SumainLei',
            sortable: true,
            filterable: false,
            width,
            align,
            headerAlign: align,
            render: (value: number | string) =>
                value ? `${parseFloat(String(value)).toFixed(0)} LEI` : '-',
        },
        {
            key: 'SumainEuro',
            title: t('contracts.amountEur'),
            dataIndex: 'SumainEuro',
            sortable: true,
            filterable: false,
            width,
            align,
            headerAlign: align,
            render: (value: number | string) =>
                value ? `${parseFloat(String(value)).toFixed(0)} EUR` : '-',
        },
    ];
};

/**
 * Creates a single date column definition for contract grids.
 */
export const createDateColumn = (key: string, title: string): ContractDateColumn => ({
    key,
    title,
    dataIndex: key,
    sortable: true,
    filterable: false,
    width: 100,
    align: 'left',
    headerAlign: 'left',
    render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
});

/**
 * Shared date columns for contract grids (Termen_restituire, Data_Exp).
 * Used in both Contracts.tsx and Payment.tsx.
 */
export const getContractDateColumns = (t: TFunction): ContractDateColumn[] => [
    createDateColumn('Termen_restituire', t('contracts.dueDate')),
    createDateColumn('Data_Exp', t('contracts.expiry')),
];
